import React, { useState } from 'react';
import './Button.scss';
import DateTimePicker from './DateTimePicker';
import Button from './Button';
import { add, remove } from '../services/session.service';
import { add as addShowing } from '../services/showing.service';
import { add as addPage } from '../services/page.service';
import { add as addSessionPage } from '../services/sessionPage.service';
import {
  AddSessionRequest,
  ColourStyleEnum,
  FontStyleEnum
} from '../models/api';
import { useNavigate } from 'react-router-dom';
import EventStyleEditor from './EventStyleEditor';
import Feedback from './Feedback';
import { ReactComponent as BackIcon } from '../assets/chevron_backward_large.svg';
import { ReactComponent as NextIcon } from '../assets/chevron_forward_large.svg';
import { ReactComponent as CheckIcon } from '../assets/check.svg';
import { ErrorDetail } from '../services/models';

const CreateNewEvent: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [eventName, setEventName] = useState('');
  const [hasDate, setHasDate] = useState(false);
  const [startDateUtc, setStartDateUtc] = useState<Date | null>(null);
  const [fontStyleEnum, setFontStyleEnum] = useState(FontStyleEnum.Classic);
  const [colourStyleEnum, setColourStyleEnum] = useState(
    ColourStyleEnum.Purple
  );

  const [errors, setErrors] = useState<ErrorDetail[]>([]);
  const [wizardStep, setWizardStep] = useState(1);

  const navigate = useNavigate();

  const checkValidation = (step?: number | undefined) => {
    setErrors([]);

    let clientValidation = true;

    if (step === undefined || step === 1) {
      if (eventName.trim() === '') {
        setErrors((p) => [...p, { message: 'Event name is required' }]);
        clientValidation = false;
      }

      if (hasDate && !startDateUtc) {
        setErrors((p) => [...p, { message: 'Start date is required' }]);
        clientValidation = false;
      }
    }

    return clientValidation;
  };

  const addNewPage = async (reference: string) => {
    const page = await addPage({});

    if (page.success && page.data) {
      await addSessionPage(reference, {
        name: 'New Slide',
        pageReference: page.data.reference
      });
    }
  };

  const navigateToSession = async (reference: string) => {
    await addNewPage(reference);
    navigate(`/Sessions/${reference}?created=true`);
  };

  const handle = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (loading) {
      return;
    }

    if (!checkValidation()) {
      return;
    }

    setLoading(true);

    const request: AddSessionRequest = {
      name: eventName,
      allowUserNavigation: true,
      allowViewAllPages: false,
      isTour: false,
      teamReference: null,
      fontStyleEnum: fontStyleEnum,
      colourStyleEnum: colourStyleEnum
    };

    try {
      const result = await add(request);
      if (result.success && result.data) {
        setErrors([]);

        if (startDateUtc) {
          const showing = await addShowing(result.data.reference, {
            location: null,
            startDateUtc: startDateUtc?.toISOString() ?? ''
          });

          if (showing.success && showing.data) {
            await navigateToSession(result.data.reference);
          } else {
            await remove(result.data.reference);
            setErrors(showing.errorDetails || []);
          }
        } else {
          await navigateToSession(result.data.reference);
        }
      } else {
        setErrors(result.errorDetails || []);
      }
    } catch {
      setErrors([{ message: 'There was an unexpected problem' }]);
    } finally {
      setLoading(false);
    }
  };

  const step = () => {
    if (wizardStep === 1) {
      return (
        <>
          <label className="title" htmlFor="eventName">
            Event name
          </label>
          <span className="intro">
            What would you like your event to be called?
          </span>
          <input
            id="eventName"
            type="text"
            placeholder="Event name"
            required
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
          />
          <label className="title" htmlFor="start">
            Event date
          </label>
          <span className="intro">
            Do you want to set when your event is taking place?
          </span>
          <div className="checkbox-container">
            <input
              type="checkbox"
              id="hasDate"
              checked={hasDate}
              onChange={(e) => setHasDate(e.target.checked)}
            />
            <label htmlFor="hasDate">Set event date</label>
          </div>
          {hasDate && (
            <>
              <span className="intro">When is your event taking place?</span>
              <div className="date-range">
                <DateTimePicker
                  initialDate={startDateUtc}
                  dateTitle="Start"
                  onDateChange={(e) => setStartDateUtc(e)}
                  id="start"
                />
              </div>
            </>
          )}
          <p>You&apos;ll be able to make changes to these details later.</p>
        </>
      );
    } else if (wizardStep === 2) {
      return (
        <>
          {wizardStep > 1 && (
            <Button type="Link" onClick={() => setWizardStep(1)}>
              <BackIcon /> Back
            </Button>
          )}
          <EventStyleEditor
            colourStyleEnum={colourStyleEnum}
            setColourStyleEnum={setColourStyleEnum}
            setFontStyleEnum={setFontStyleEnum}
            fontStyleEnum={fontStyleEnum}
            isCreation={true}
          />
        </>
      );
    }
    return null;
  };

  return (
    <div className="page-form">
      <h1>Create event</h1>
      <div className="steps">
        <div
          className={`step ${wizardStep === 1 ? 'step__selected' : ''} ${wizardStep === 2 ? 'step__completed' : ''}`}
        >
          <div className="circle">{wizardStep === 2 ? <CheckIcon /> : '1'}</div>{' '}
          <span>Name and date</span>
        </div>
        <div className={`step ${wizardStep === 2 ? 'step__selected' : ''}`}>
          <div className="circle">2</div> <span>Theme</span>
        </div>
      </div>
      <form onSubmit={handle} className="content-form-full">
        {step()}
        <div className="wizard-footer">
          {wizardStep === 1 && (
            <>
              <div className="button-container button-container__primary">
                <Button onClick={() => checkValidation(1) && setWizardStep(2)}>
                  Next: Theme <NextIcon />
                </Button>
              </div>
              <div className="button-container button-container__secondary">
                <Button type="Tertiary" onClick={() => navigate('/dashboard')}>
                  Cancel
                </Button>
              </div>
            </>
          )}
          {wizardStep === 2 && (
            <>
              <div className="button-container button-container__primary">
                <Button
                  isSubmit={true}
                  isLoading={loading}
                  loadingText="Saving"
                >
                  Next: Add content <NextIcon />
                </Button>
              </div>
              <div className="button-container button-container__secondary">
                <Button type="Tertiary" onClick={() => navigate('/dashboard')}>
                  Cancel
                </Button>
              </div>
            </>
          )}
          <Feedback messageDetails={errors} type="error" />
        </div>
      </form>
    </div>
  );
};

export default CreateNewEvent;
